import React, { useEffect, useState, useCallback } from 'react';
import { fmtWh } from './time';


import { authFetch, API } from './api';

import ProductsPage from './ProductsPage';

// The clock a shop's timestamps are in when they carry no offset (Wikinggruppen
// sends naive Stockholm times; MyCashflow and Shopify send an offset, so for
// them this is only a fallback). A setting per store, not a constant — Fernando,
// 2026-09-08, after Motley's order times showed one hour early.
const STORE_TZ_OPTIONS = ['Europe/Stockholm', 'Europe/Helsinki', 'Europe/Tallinn', 'Europe/Riga',
                          'Europe/Vilnius', 'Europe/Oslo', 'Europe/Copenhagen', 'Europe/Berlin', 'UTC'];


const PLATFORMS = [
  { key: 'mcf', label: 'MyCashflow' },
  { key: 'wgr', label: 'Wikinggruppen' },
  { key: 'shopify', label: 'Shopify' },
  { key: 'other', label: 'Other' },
];

interface Webshop {
  id: string;
  name: string;
  platform: string;
  config: Record<string, any>;
}

// ── Add store form ────────────────────────────────────────────────────────────

function AddStoreForm({ onSave, onCancel }: {
  onSave: (name: string, platform: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState('mcf');

  return (
    <div className="bg-gray-900/60 rounded-xl border border-gray-700 p-4 max-w-sm">
      <div className="space-y-3 mb-3">
        <div>
          <label className="text-gray-400 text-xs uppercase tracking-wider">Store name</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Big Oliver"
            autoFocus
            className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div>
          <label className="text-gray-400 text-xs uppercase tracking-wider">Platform</label>
          <select
            value={platform}
            onChange={e => setPlatform(e.target.value)}
            className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
          >
            {PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => name.trim() && onSave(name.trim(), platform)}
          disabled={!name.trim()}
          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm rounded-lg"
        >
          Add store
        </button>
        <button onClick={onCancel} className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── General tab ───────────────────────────────────────────────────────────────

// Product edits written back to this shop (Fernando, 2026-09-08). The switch is
// the consent for automatic outward writes; the field lists come from
// /api/products/columns — the same matrix the relays send from — so this card
// can never promise a sync that does not happen.
interface PushStoreStatus {
  webshop_id: string; name: string; platform: string; on: boolean;
  fields: { column: string; platform_field: string; resource: string }[];
  wms_only: Record<string, string>;
  pending?: number; retrying?: number; sending?: number; failed?: number;
  sent_today?: number; sent_total?: number; last_ok?: string | null;
}
// Edit an order from the WMS (2026-10-05): the store's own switch — the order panel's "Edit order…" and the
// backend both read config.order_edits. What each platform can do: docs/ORDER-EDIT.md.
const ORDER_EDIT_NOTE: Record<string, string> = {
  shopify: 'Size swap, quantity and removal through Shopify order editing. Shopify moves its own stock; the customer gets no e-mail.',
  mcf: 'Quantity and removal only (MyCashflow cannot add a catalog size by API). Switch on only after the live test on a test order.',
  wgr: "Size swap, quantity and removal by re-sending the order lines; the WMS writes the stock. Switch on only after the live test and WGR's answer about customer e-mails.",
};
function OrderEditCard({ webshopId }: { webshopId: string }) {
  const [ws, setWs] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = (localStorage.getItem('wms_role') || '') === 'admin';
  const load = useCallback(async () => {
    const r = await authFetch(`${API}/settings/webshops`);
    if (!r.ok) return;
    const all = await r.json();
    setWs(all.find((w: any) => w.id === webshopId) || null);
  }, [webshopId]);
  useEffect(() => { load(); }, [load]);
  if (!ws) return null;
  const on = String(ws.config?.order_edits ?? '').toLowerCase() === 'true';
  const toggle = async () => {
    setSaving(true);
    // read-merge-patch of the whole config, like the write-back switch: never drops a credential
    const r = await authFetch(`${API}/settings/webshops`);
    const all = await r.json();
    const cur = all.find((w: any) => w.id === webshopId);
    await authFetch(`${API}/settings/webshops/${webshopId}`, {
      method: 'PATCH', body: JSON.stringify({ config: { ...cur?.config, order_edits: !on } }),
    });
    setSaving(false);
    load();
  };
  return (
    <div>
      <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">Order edits from the WMS</div>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 flex items-start justify-between gap-4">
        <div>
          <div className="text-white text-sm font-medium">Managers and admins can change an order's lines from the order page — the change is made in {ws.name} itself.</div>
          <p className="text-gray-600 text-xs mt-1">{ORDER_EDIT_NOTE[ws.platform] || 'Not supported for this platform.'}</p>
        </div>
        {isAdmin && ORDER_EDIT_NOTE[ws.platform] && (
          <button onClick={toggle} disabled={saving}
            className={`shrink-0 px-4 py-1.5 text-sm rounded-lg font-medium ${on ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300'} disabled:opacity-50`}>
            {on ? 'On' : 'Off'}
          </button>
        )}
      </div>
    </div>
  );
}

function ProductPushCard({ webshopId }: { webshopId: string }) {
  const isAdmin = (localStorage.getItem('wms_role') || 'staff') === 'admin';
  const [status, setStatus] = useState<PushStoreStatus | null>(null);
  const [schema, setSchema] = useState(true);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [count, setCount] = useState<{ count: number; by_field: Record<string, number>; minutes: number } | null>(null);

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/products/pushes/status`);
    if (!r.ok) return;
    const d = await r.json();
    setSchema(d.schema !== false);
    setStatus((d.stores || []).find((s: PushStoreStatus) => s.webshop_id === webshopId) || null);
    const c = await authFetch(`${API}/products/columns?webshop_id=${encodeURIComponent(webshopId)}`);
    if (c.ok) {
      const cd = await c.json();
      const m: Record<string, string> = {};
      for (const col of cd.columns || []) m[col.key] = col.label;
      setLabels(m);
    }
  }, [webshopId]);
  useEffect(() => { setMsg(null); setCount(null); load(); }, [load]);

  const toggle = async (on: boolean) => {
    setSaving(true);
    // read-merge-patch of the whole config: the credentials form saves its own
    // block and must not be able to drop this flag, nor this flag its secrets
    const r = await authFetch(`${API}/settings/webshops`);
    const all = await r.json();
    const ws = all.find((w: any) => w.id === webshopId);
    await authFetch(`${API}/settings/webshops/${webshopId}`, {
      method: 'PATCH',
      body: JSON.stringify({ config: { ...ws?.config, push_product_edits: on } }),
    });
    setSaving(false);
    setMsg(on ? 'On. Edits made from now on are sent to the shop within a minute.'
              : 'Off. Edits stay in the WMS; rows already queued wait until it is switched on again.');
    load();
  };

  const countBackfill = async () => {
    const r = await authFetch(`${API}/products/pushes/backfill/${webshopId}`, {
      method: 'POST', body: JSON.stringify({ confirm: false }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || 'Could not count.'); return; }
    setCount(d);
  };
  const runBackfill = async () => {
    if (!count) return;
    if (!window.confirm(`Send ${count.count} WMS-owned values to ${status?.name}? About ${Math.max(1, count.minutes)} minute(s) at 30 a minute.`)) return;
    const r = await authFetch(`${API}/products/pushes/backfill/${webshopId}`, {
      method: 'POST', body: JSON.stringify({ confirm: true }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || 'Could not queue.'); return; }
    setMsg(`${d.queued} values queued. They go out at about 30 a minute; each cell on the Products page shows ⟳ until the shop confirms.`);
    setCount(null);
    load();
  };

  if (!status) return null;
  const fields = status.fields || [];
  return (
    <div>
      <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">Send product edits to the shop</div>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-4">
        {!schema && (
          <p className="text-amber-400 text-xs">Not active yet: migration 025 (product_field_pushes) has not been applied.</p>
        )}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-white text-sm font-medium">
              {fields.length
                ? `A field edited on the Products page is written to ${status.name} when ${status.platform.toUpperCase()} has a field for it.`
                : `${status.platform.toUpperCase()} has no field the WMS can write to.`}
            </div>
            <p className="text-gray-600 text-xs mt-1">
              Off means nothing is sent — not later, not on switch-on. Values already set in the WMS are sent only
              with the button below, after you have seen the count. Clearing a field never blanks the shop&apos;s value.
            </p>
          </div>
          {isAdmin && fields.length > 0 && (
            <button onClick={() => toggle(!status.on)} disabled={saving || !schema}
              className={`shrink-0 px-4 py-1.5 text-sm rounded-lg font-medium ${
                status.on ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300'} disabled:opacity-50`}>
              {status.on ? 'On' : 'Off'}
            </button>
          )}
        </div>

        {fields.length > 0 && (
          <div className="grid md:grid-cols-2 gap-4 text-xs">
            <div>
              <div className="text-gray-500 uppercase tracking-wider mb-1">Written to {status.name} <span className="text-emerald-400/80">↔</span></div>
              <ul className="space-y-0.5">
                {fields.map(f => (
                  <li key={f.column} className="text-gray-300">
                    {labels[f.column] || f.column} <span className="text-gray-600">→ {f.platform_field}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-gray-500 uppercase tracking-wider mb-1">Stays in the WMS</div>
              <ul className="space-y-0.5">
                {Object.entries(status.wms_only || {}).map(([col, why]) => (
                  <li key={col} className="text-gray-500">
                    {labels[col] || col} <span className="text-gray-700">— {why}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {schema && fields.length > 0 && (
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400 border-t border-gray-800 pt-3">
            <span>Waiting: <span className="text-gray-200">{status.pending ?? 0}</span></span>
            <span>Retrying: <span className="text-gray-200">{status.retrying ?? 0}</span></span>
            <span>Sent today: <span className="text-gray-200">{status.sent_today ?? 0}</span></span>
            <span>Total sent: <span className="text-gray-200">{status.sent_total ?? 0}</span></span>
            <span>Last: <span className="text-gray-200">{status.last_ok ? fmtWh(status.last_ok, 'short') : '—'}</span></span>
            <span>Not sent: <span className={status.failed ? 'text-red-400' : 'text-gray-200'}>{status.failed ?? 0}</span>
              {!!status.failed && <span className="text-gray-600"> — see Review</span>}</span>
          </div>
        )}

        {isAdmin && schema && fields.length > 0 && (
          <div className="border-t border-gray-800 pt-3 flex flex-wrap items-center gap-3">
            {!count ? (
              <button onClick={countBackfill}
                className="px-3 py-1.5 text-xs rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200">
                Count WMS-owned values not yet in the shop
              </button>
            ) : (
              <>
                <span className="text-xs text-gray-300">
                  {count.count} value{count.count === 1 ? '' : 's'}
                  {count.count > 0 && <> ({Object.entries(count.by_field).map(([f, n]) => `${labels[f] || f} ${n}`).join(', ')}) — about {Math.max(1, count.minutes)} min</>}
                </span>
                {count.count > 0 && (
                  <button onClick={runBackfill} disabled={!status.on}
                    title={status.on ? undefined : 'Switch write-back on first'}
                    className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">
                    Send them
                  </button>
                )}
                <button onClick={() => setCount(null)} className="text-xs text-gray-500 hover:text-white">Cancel</button>
              </>
            )}
          </div>
        )}
        {msg && <p className="text-xs text-gray-300">{msg}</p>}
      </div>
    </div>
  );
}

function GeneralTab({ webshopId, webshopName, platform }: {
  webshopId: string;
  webshopName: string;
  platform: string;
}) {
  const [baseUrl, setBaseUrl] = useState('');
  const [storeTz, setStoreTz] = useState('');
  const [username, setUsername] = useState('');
  const [apiKey, setApiKey] = useState('');
  // "a value is stored" — never the value itself
  const [apiKeySet, setApiKeySet] = useState(false);
  const [wgrUrl, setWgrUrl] = useState('');
  const [wgrTarget, setWgrTarget] = useState('');
  const [wgrUser, setWgrUser] = useState('');
  const [wgrPass, setWgrPass] = useState('');
  const [wgrPassSet, setWgrPassSet] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testOk, setTestOk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [syncing, setSyncing] = useState<'orders' | 'products' | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<Record<string, string>>({});

  const loadSyncStatus = useCallback(async () => {
    const res = await authFetch(`${API}/sync/status`);
    if (!res.ok) return;
    const rows: Array<{ webshop_id: string; sync_type: string; finished_at: string | null; error: string | null }> = await res.json();
    const map: Record<string, string> = {};
    for (const row of rows) {
      if (row.webshop_id === webshopId) {
        map[row.sync_type] = row.error
          ? `Failed: ${row.error.slice(0, 60)}`
          : row.finished_at
            ? `Last synced ${fmtWh(row.finished_at, 'short')}`
            : 'In progress…';
      }
    }
    setLastSync(map);
  }, [webshopId]);

  const loadCreds = useCallback(async () => {
    const res = await authFetch(`${API}/settings/webshops`);
    const webshops: Webshop[] = await res.json();
    const ws = webshops.find(w => w.id === webshopId);
    setStoreTz(ws?.config?.timezone ?? '');
    const mcf = ws?.config?.mcf ?? {};
    setBaseUrl(mcf.base_url ?? '');
    setUsername(mcf.username ?? '');
    // Secrets are never sent to the browser and are never put in the page. The
    // field stays EMPTY and the placeholder says a value is stored; saving it
    // blank keeps what is stored (see _merge_secrets). Leaving a real password
    // in an input next to a username field is also exactly what made Chrome
    // offer to save our WGR API password on a page nobody had typed into.
    setApiKeySet(Boolean(mcf.api_key));
    setApiKey('');
    const wgr = ws?.config?.wgr ?? {};
    setWgrUrl(wgr.api_url ?? '');
    setWgrTarget(wgr.api_target ?? '');
    setWgrUser(wgr.username ?? '');
    setWgrPassSet(Boolean(wgr.password));
    setWgrPass('');
  }, [webshopId]);

  useEffect(() => {
    setSaved(false);
    setSyncResult(null);
    setTestResult(null);
    loadCreds();
    loadSyncStatus();
  }, [webshopId, loadCreds, loadSyncStatus]);

  const handleSave = async () => {
    setSaving(true);
    const res = await authFetch(`${API}/settings/webshops`);
    const webshops: Webshop[] = await res.json();
    const ws = webshops.find(w => w.id === webshopId);
    const section = platform === 'wgr'
      ? { wgr: { api_url: wgrUrl, api_target: wgrTarget, username: wgrUser, password: wgrPass } }
      : { mcf: { base_url: baseUrl, username, api_key: apiKey } };
    const mergedConfig = { ...ws?.config, ...section, ...(storeTz ? { timezone: storeTz } : {}) };
    await authFetch(`${API}/settings/webshops/${webshopId}`, {
      method: 'PATCH',
      body: JSON.stringify({ config: mergedConfig }),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setTestOk(false);
    try {
      const res = await authFetch(`${API}/settings/webshops/${webshopId}/test-connection`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        setTestResult(data.detail ?? 'Connection failed');
      } else {
        const orders = data.picker2_count > 0 ? ` — order${data.picker2_count > 1 ? 's' : ''} ${data.picker2_orders.join(', ')}` : '';
        setTestOk(true);
        setTestResult(`Connected. ${data.statuses.length} order statuses found. Picker #2 tab: ${data.picker2_count} order(s)${orders}`);
      }
    } catch {
      setTestResult('Connection failed — check server logs');
    }
    setTesting(false);
  };

  const syncOrders = async () => {
    setSyncing('orders');
    setSyncResult(null);
    try {
      const res = await authFetch(`${API}/sync`, { method: 'POST', body: JSON.stringify({ webshop_id: webshopId }) });
      const data = await res.json();
      setSyncResult(`Synced ${data.synced ?? 0} orders`);
      loadSyncStatus();
    } catch {
      setSyncResult('Sync failed — check server logs');
    }
    setSyncing(null);
  };

  const syncProducts = async () => {
    setSyncing('products');
    setSyncResult(null);
    try {
      const res = await authFetch(`${API}/sync-products`, { method: 'POST', body: JSON.stringify({ webshop_id: webshopId }) });
      const data = await res.json();
      setSyncResult(`Synced ${data.synced_products ?? 0} products, ${data.synced_variations ?? 0} variations`);
      loadSyncStatus();
    } catch {
      setSyncResult('Sync failed — check server logs');
    }
    setSyncing(null);
  };

  return (
    <div className="space-y-6">
      {/* Store info */}
      <div>
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">Store</div>
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4 flex items-center gap-4">
          <div className="flex-1">
            <div className="text-white font-semibold">{webshopName}</div>
            <div className="text-gray-500 text-xs mt-0.5">ID: {webshopId}</div>
          </div>
          <span className="text-xs px-2 py-1 rounded-lg bg-gray-800 text-gray-400 uppercase tracking-wider">
            {platform}
          </span>
        </div>
      </div>

      {/* Store timezone */}
      <div>
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">Store timezone</div>
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
          <label className="text-gray-500 text-xs uppercase tracking-wider">Clock of the shop&apos;s timestamps</label>
          <select value={storeTz} onChange={e => setStoreTz(e.target.value)}
            className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500">
            <option value="">— not set (warehouse timezone) —</option>
            {STORE_TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
          <p className="text-gray-600 text-xs mt-2">
            Used to read order times that arrive without an offset (Wikinggruppen sends Stockholm time).
            The WMS stores every order time as one instant and shows it on the warehouse clock. Saved with the button below.
          </p>
        </div>
      </div>

      <ProductPushCard webshopId={webshopId} />
      <OrderEditCard webshopId={webshopId} />

      {/* MCF credentials */}
      {platform === 'mcf' && (
        <div>
          <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">
            MyCashflow API
          </div>
          <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-4">
            <div>
              <label className="text-gray-500 text-xs uppercase tracking-wider">Store URL</label>
              <input
                value={baseUrl}
                onChange={e => setBaseUrl(e.target.value)}
                placeholder="https://yourstore.mycashflow.fi"
                className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-gray-500 text-xs uppercase tracking-wider">API Username</label>
                <input
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="api username"
                  autoComplete="off"
                  className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-gray-500 text-xs uppercase tracking-wider">API Key</label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  placeholder={apiKeySet ? 'stored — leave blank to keep' : 'API key'}
                  autoComplete="new-password"
                  className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="pt-1 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                  saved
                    ? 'bg-green-600 text-white cursor-default'
                    : 'bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white'
                }`}
              >
                {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save credentials'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WGR credentials */}
      {platform === 'wgr' && (
        <div>
          <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">
            Wikinggruppen API
          </div>
          <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-gray-500 text-xs uppercase tracking-wider">API URL</label>
                <input
                  value={wgrUrl}
                  onChange={e => setWgrUrl(e.target.value)}
                  placeholder="https://www.yourstore.se/api/v1/"
                  className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-gray-500 text-xs uppercase tracking-wider">Forwarding target (optional)</label>
                <input
                  value={wgrTarget}
                  onChange={e => setWgrTarget(e.target.value)}
                  placeholder="only if API URL is WGR's forwarding service"
                  className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-gray-500 text-xs uppercase tracking-wider">API Username</label>
                <input
                  value={wgrUser}
                  onChange={e => setWgrUser(e.target.value)}
                  placeholder="api username"
                  autoComplete="off"
                  className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-gray-500 text-xs uppercase tracking-wider">API Password</label>
                <input
                  type="password"
                  value={wgrPass}
                  onChange={e => setWgrPass(e.target.value)}
                  placeholder={wgrPassSet ? 'stored — leave blank to keep' : 'API password'}
                  autoComplete="new-password"
                  className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="pt-1 flex items-center justify-between gap-3">
              <button
                onClick={testConnection}
                disabled={testing || !wgrUrl || !wgrUser}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {testing ? 'Testing…' : 'Test connection'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                  saved
                    ? 'bg-green-600 text-white cursor-default'
                    : 'bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white'
                }`}
              >
                {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save credentials'}
              </button>
            </div>
            {testResult && (
              <p className={`text-sm ${testOk ? 'text-green-400' : 'text-red-400'}`}>{testResult}</p>
            )}
          </div>
        </div>
      )}

      {/* Sync */}
      {platform === 'wgr' ? (
        <div>
          <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">Sync</div>
          <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-white text-sm font-medium">Orders</div>
              <div className="text-gray-500 text-xs mt-0.5">
                {lastSync["orders"] ?? `Synced automatically like the other stores; this button forces one pass now. Products come from the nightly WGR article sync, not this page.`}
              </div>
            </div>
            <button
              onClick={syncOrders}
              disabled={syncing !== null}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {syncing === 'orders' ? 'Syncing…' : 'Sync orders'}
            </button>
          </div>
          {syncResult && (
            <p className="text-green-400 text-sm mt-2">{syncResult}</p>
          )}
        </div>
      ) : (
      <div>
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">Sync</div>
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 divide-y divide-gray-800">
          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-white text-sm font-medium">Orders</div>
              <div className="text-gray-500 text-xs mt-0.5">{lastSync["orders"] ?? `Pull open orders from ${webshopName}`}</div>
            </div>
            <button
              onClick={syncOrders}
              disabled={syncing !== null}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {syncing === 'orders' ? 'Syncing…' : 'Sync orders'}
            </button>
          </div>
          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-white text-sm font-medium">Products</div>
              <div className="text-gray-500 text-xs mt-0.5">{lastSync["products"] ?? `Pull product catalog and variations from ${webshopName}`}</div>
            </div>
            <button
              onClick={syncProducts}
              disabled={syncing !== null}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {syncing === 'products' ? 'Syncing…' : 'Sync products'}
            </button>
          </div>
        </div>
        {syncResult && (
          <p className="text-green-400 text-sm mt-2">{syncResult}</p>
        )}
      </div>
      )}
    </div>
  );
}

// ── Global order-sync cadence ────────────────────────────────────────────────
// One background loop syncs ALL stores in sequence, so the interval is a single
// app-wide setting — putting it inside a store's card would suggest it isn't.

function OrderSyncCard() {
  const isAdmin = (localStorage.getItem('wms_role') || 'staff') === 'admin';
  const [secs, setSecs] = useState<string>('');
  const [range, setRange] = useState<{ min: number; max: number }>({ min: 30, max: 3600 });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    authFetch(`${API}/order-sync-settings`).then(r => r.json()).then(d => {
      setSecs(String(d.interval_seconds ?? 60));
      if (d.min && d.max) setRange({ min: d.min, max: d.max });
    });
  }, []);

  const save = async () => {
    setSaving(true); setMsg('');
    const res = await authFetch(`${API}/order-sync-settings`, {
      method: 'PUT', body: JSON.stringify({ interval_seconds: Number(secs) }),
    });
    const d = await res.json().catch(() => ({}));
    setSaving(false);
    if (!res.ok) { setMsg(d.detail || 'Could not save'); return; }
    setMsg('✓ Saved — applies from the next sync cycle, no restart needed.');
    setTimeout(() => setMsg(''), 4000);
  };

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4 mb-6 flex items-center justify-between gap-4 flex-wrap">
      <div>
        <div className="text-white text-sm font-medium">Automatic order sync — all stores</div>
        <div className="text-gray-500 text-xs mt-0.5">
          How often the WMS pulls orders and follows shop-side changes (cancellations included).
          One loop covers every store; a full pass takes a few seconds.
        </div>
        {msg && <div className={`text-xs mt-1 ${msg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{msg}</div>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gray-400 text-sm">every</span>
        <input
          type="number" min={range.min} max={range.max} value={secs}
          disabled={!isAdmin}
          onChange={e => setSecs(e.target.value)}
          className="w-24 px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm text-right focus:outline-none focus:border-blue-500 disabled:opacity-50"
        />
        <span className="text-gray-400 text-sm">seconds</span>
        {isAdmin && (
          <button onClick={save} disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main StoresPage ───────────────────────────────────────────────────────────

type StoreSubTab = 'general' | 'shipping' | 'products';

export default function StoresPage() {
  const [webshops, setWebshops] = useState<Webshop[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [subTab, setSubTab] = useState<StoreSubTab>('general');
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/settings/webshops`);
    const data: Webshop[] = await res.json();
    setWebshops(data);
    return data;
  }, []);

  useEffect(() => {
    load().then(data => {
      if (data.length > 0) setSelectedId(prev => prev ?? data[0].id);
    });
  }, []);

  const handleAdd = async (name: string, platform: string) => {
    const res = await authFetch(`${API}/settings/webshops`, {
      method: 'POST',
      body: JSON.stringify({ name, platform }),
    });
    const created: Webshop = await res.json();
    setAdding(false);
    await load();
    setSelectedId(created.id);
    setSubTab('general');
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    await authFetch(`${API}/settings/webshops/${selectedId}`, { method: 'DELETE' });
    setConfirmDelete(false);
    const data = await load();
    setSelectedId(data.length > 0 ? data[0].id : null);
  };

  const selected = webshops.find(w => w.id === selectedId);

  const subTabs: { key: StoreSubTab; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'shipping', label: 'Shipping' },
    { key: 'products', label: 'Products' },
  ];

  return (
    <div>
      <p className="text-gray-500 text-sm mb-5">
        Manage connected stores, API credentials, carriers, and product catalogs.
      </p>

      <OrderSyncCard />

      {/* Store selector */}
      <div className="flex items-start gap-2 mb-6 flex-wrap">
        {webshops.map(ws => (
          <button
            key={ws.id}
            onClick={() => { setSelectedId(ws.id); setConfirmDelete(false); setSubTab('general'); }}
            className={`px-4 py-2 text-sm rounded-lg border transition-colors flex items-center gap-2 ${
              selectedId === ws.id
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            {ws.name}
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              selectedId === ws.id ? 'bg-blue-700 text-blue-200' : 'bg-gray-700 text-gray-500'
            }`}>
              {ws.platform.toUpperCase()}
            </span>
          </button>
        ))}
        {adding ? (
          <div className="flex-1 min-w-[340px]">
            <AddStoreForm onSave={handleAdd} onCancel={() => setAdding(false)} />
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 border-dashed text-gray-400 hover:text-white text-sm rounded-lg transition-colors"
          >
            + Add store
          </button>
        )}
      </div>

      {!selected && !adding && (
        <div className="text-gray-600 text-sm py-12 text-center">
          No stores yet. Add one above to get started.
        </div>
      )}

      {selected && (
        <>
          {/* Sub-tab bar */}
          <div className="flex gap-1 border-b border-gray-800 mb-6">
            {subTabs.map(t => (
              <button
                key={t.key}
                onClick={() => setSubTab(t.key)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  subTab === t.key ? 'text-white border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Sub-tab content */}
          {subTab === 'general' && (
            <div className="space-y-6">
              <GeneralTab
                webshopId={selected.id}
                webshopName={selected.name}
                platform={selected.platform}
              />
              <div className="pt-4 border-t border-gray-800">
                {confirmDelete ? (
                  <div className="flex items-center gap-2">
                    <span className="text-red-400 text-sm">Remove "{selected.name}"? This cannot be undone.</span>
                    <button onClick={handleDelete} className="px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-sm rounded-lg">
                      Yes, remove
                    </button>
                    <button onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="text-gray-700 hover:text-red-400 text-sm transition-colors"
                  >
                    Remove store
                  </button>
                )}
              </div>
            </div>
          )}

          {subTab === 'shipping' && (
            <div className='text-gray-500 text-sm py-4'>Shipping settings have moved to Settings → Shipping.</div>
          )}

          {subTab === 'products' && (
            <ProductsPage webshopId={selectedId!} />
          )}
        </>
      )}
    </div>
  );
}
